import { useEffect, useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import { useApi, useApiMutation } from "@/hooks/use-api";
import { createSegment, useSegments } from "@/hooks/use-segments";
import { createEmailTemplate } from "@/hooks/use-email-templates";
import { createCampaign, sendCampaign, scheduleCampaign } from "@/hooks/use-campaigns";
import { sendAiFeedbackSignal } from "@/lib/ai-feedback";
import { TemplateLibrary, type EmailTemplate } from "./template-library";
import { EmailPreview } from "./email-preview";
import {
  convertZonedLocalToUtcIso,
  formatScheduledSummary,
  getBrowserTimezone,
  listSupportedTimezones,
  resolveOptimalSendUtc,
  type SendOption,
} from "./campaign-scheduling";
import { EmbeddedEmailBuilder, type EmbeddedEmailBuilderChange } from "@/routes/communications/email-builder";
import {
  createEmailModeComponent,
  type EmailModePuckComponent,
} from "@ease/page-builder";
import { sanitizeCampaignHtml } from "./html-sanitizer";

type WizardStep = "audience" | "content" | "schedule" | "review";
const STEPS: WizardStep[] = ["audience", "content", "schedule", "review"];
type ContentMode = "visual" | "html";

interface CampaignWizardProps {
  onClose: () => void;
  onCreated: () => void;
}

interface SendTimeResponse {
  recommendation: {
    optimalHour: number;
    confidence: "high" | "medium" | "low";
    distribution: Array<{ hour: number; score: number }>;
  } | null;
  heatmap: Array<{
    day: number;
    hour: number;
    deliveredCount: number;
    openCount: number;
    openRate: number;
  }>;
  provenance: {
    dataStatus: "sufficient" | "insufficient" | "none";
    daysSinceFirstTouch: number | null;
    message: string;
  };
}

interface CampaignPredictionResponse {
  prediction: {
    predictedOpenRatePct: number;
    predictedClickRatePct: number;
    confidence: "high" | "medium" | "low";
    factors: string[];
  };
  audience: {
    estimatedRecipients: number;
    historicalSampleSize: number;
  };
  recommendations: string[];
}

/** Email campaign creation wizard: audience → content → schedule → review → send. */
export function CampaignWizard({ onClose, onCreated }: CampaignWizardProps) {
  const [step, setStep] = useState<WizardStep>("audience");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [contentNotice, setContentNotice] = useState<string | null>(null);
  const [contentNoticeTone, setContentNoticeTone] = useState<"neutral" | "success" | "error">("neutral");

  const [name, setName] = useState("");
  const [segmentId, setSegmentId] = useState("");
  const [subject, setSubject] = useState("");
  const [htmlContent, setHtmlContent] = useState("<p>Hello {{firstName}},</p><p>Your content here...</p>");
  const [textContent, setTextContent] = useState("");
  const [contentMode, setContentMode] = useState<ContentMode>("visual");
  const [builderBlocks, setBuilderBlocks] = useState<EmailModePuckComponent[]>(() => createStarterBlocks());
  const [sendOption, setSendOption] = useState<SendOption>("now");
  const [scheduledAt, setScheduledAt] = useState("");
  const [scheduleTimezone, setScheduleTimezone] = useState(getBrowserTimezone());
  const [reviewApproved, setReviewApproved] = useState(false);
  const [inlineSegments, setInlineSegments] = useState<Array<{ id: string; name: string; cachedCount: number | null }>>([]);

  const { segments, refetch: refetchSegments } = useSegments();
  const sendTime = useApi<SendTimeResponse>("/email-marketing/send-time");
  const currentIdx = STEPS.indexOf(step);
  const availableSegments = useMemo(() => {
    const byId = new Map<string, { id: string; name: string; cachedCount: number | null }>();

    for (const segment of segments ?? []) {
      byId.set(segment.id, segment);
    }

    for (const segment of inlineSegments) {
      byId.set(segment.id, segment);
    }

    return [...byId.values()];
  }, [inlineSegments, segments]);

  const supportedTimezones = useMemo(() => listSupportedTimezones(), []);
  const optimalHour = sendTime.data?.recommendation?.optimalHour ?? null;

  useEffect(() => {
    setReviewApproved(false);
  }, [htmlContent, scheduledAt, scheduleTimezone, segmentId, sendOption, subject, textContent]);

  const handleCreateSegment = async (input: { name: string; description?: string; type: "dynamic" | "static" }) => {
    const created = await createSegment(input);
    setInlineSegments((current) => {
      const remaining = current.filter((segment) => segment.id !== created.id);
      return [...remaining, { id: created.id, name: created.name, cachedCount: created.cachedCount }];
    });
    setSegmentId(created.id);
    await refetchSegments();
  };

  const canNext = () => {
    if (step === "audience") return Boolean(name.trim() && segmentId);
    if (step === "content") return Boolean(subject.trim() && htmlContent.trim());
    if (step === "schedule") {
      if (sendOption === "now") return true;
      if (sendOption === "optimal") return optimalHour !== null;
      return Boolean(scheduledAt && scheduleTimezone);
    }
    return true;
  };

  const handleSend = async () => {
    if (!reviewApproved) {
      setContentNoticeTone("error");
      setContentNotice("Confirm human review before sending or scheduling this campaign.");
      return;
    }

    setIsSubmitting(true);
    try {
      const campaignTemplate = await createEmailTemplate({
        name: `${name.trim()} campaign template`,
        subject: subject.trim(),
        htmlContent,
        textContent: textContent.trim() || null,
        category: "marketing",
      });

      const campaign = await createCampaign({
        name: name.trim(),
        subject: subject.trim(),
        segmentId,
        templateId: campaignTemplate.id,
      });

      if (sendOption === "scheduled" && scheduledAt) {
        await scheduleCampaign(
          campaign.id,
          convertZonedLocalToUtcIso(scheduledAt, scheduleTimezone),
        );
      } else if (sendOption === "optimal" && optimalHour !== null) {
        await scheduleCampaign(
          campaign.id,
          resolveOptimalSendUtc(optimalHour, scheduleTimezone),
        );
      } else {
        await sendCampaign(campaign.id);
      }

      onCreated();
    } catch {
      setContentNoticeTone("error");
      setContentNotice("Campaign creation failed. Review the content and scheduling fields, then try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSelectTemplate = (template: EmailTemplate) => {
    setSubject(template.subject);
    setHtmlContent(sanitizeCampaignHtml(template.htmlContent));
    setTextContent(template.textContent ?? "");
    setContentMode("html");
    setContentNoticeTone("success");
    setContentNotice(`Loaded template: ${template.name}`);
  };

  const handleSaveTemplate = async () => {
    try {
      const savedTemplate = await createEmailTemplate({
        name: `${name.trim() || subject.trim() || "Campaign"} reusable template`,
        subject: subject.trim(),
        htmlContent,
        textContent: textContent.trim() || null,
        category: "marketing",
      });

      setContentNoticeTone("success");
      setContentNotice(`Saved reusable template: ${savedTemplate.name}`);
    } catch {
      setContentNoticeTone("error");
      setContentNotice("Saving the reusable template failed.");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-5xl rounded-lg bg-card shadow-xl">
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <h2 className="text-lg font-semibold">New Campaign</h2>
          <button type="button" onClick={onClose} className="text-muted-foreground hover:text-foreground">✕</button>
        </div>

        <div className="flex border-b border-border">
          {STEPS.map((wizardStep, index) => (
            <button
              key={wizardStep}
              type="button"
              onClick={() => index <= currentIdx && setStep(wizardStep)}
              className={cn(
                "flex-1 py-3 text-center text-sm font-medium transition-colors",
                index === currentIdx
                  ? "border-b-2 border-primary text-foreground"
                  : index < currentIdx
                    ? "cursor-pointer text-primary"
                    : "cursor-not-allowed text-muted-foreground",
              )}
            >
              {index + 1}. {wizardStep.charAt(0).toUpperCase() + wizardStep.slice(1)}
            </button>
          ))}
        </div>

        <div className="max-h-[70vh] overflow-y-auto p-6">
          {step === "audience" && (
            <AudienceStep
              name={name}
              onNameChange={setName}
              segmentId={segmentId}
              onSegmentChange={setSegmentId}
              segments={availableSegments}
              onCreateSegment={handleCreateSegment}
            />
          )}
          {step === "content" && (
            <ContentStep
              contentMode={contentMode}
              onContentModeChange={setContentMode}
              subject={subject}
              onSubjectChange={setSubject}
              builderBlocks={builderBlocks}
              onBuilderBlocksChange={setBuilderBlocks}
              htmlContent={htmlContent}
              onHtmlContentChange={setHtmlContent}
              textContent={textContent}
              onTextContentChange={setTextContent}
              contentNotice={contentNotice}
              contentNoticeTone={contentNoticeTone}
              onSaveTemplate={handleSaveTemplate}
              onSelectTemplate={handleSelectTemplate}
            />
          )}
          {step === "schedule" && (
            <ScheduleStep
              sendOption={sendOption}
              onSendOptionChange={setSendOption}
              scheduledAt={scheduledAt}
              onScheduledAtChange={setScheduledAt}
              scheduleTimezone={scheduleTimezone}
              onScheduleTimezoneChange={setScheduleTimezone}
              supportedTimezones={supportedTimezones}
              sendTime={sendTime.data}
              sendTimeLoading={sendTime.isLoading}
              sendTimeError={sendTime.error}
            />
          )}
          {step === "review" && (
            <ReviewStep
              name={name}
              subject={subject}
              segmentId={segmentId}
              segmentName={availableSegments.find((segment) => segment.id === segmentId)?.name ?? "Unknown"}
              sendOption={sendOption}
              scheduledAt={scheduledAt}
              scheduleTimezone={scheduleTimezone}
              optimalHour={optimalHour}
              htmlContent={htmlContent}
              reviewApproved={reviewApproved}
              onReviewApprovedChange={setReviewApproved}
            />
          )}
        </div>

        <div className="flex justify-between border-t border-border px-6 py-4">
          <button
            type="button"
            onClick={() => {
              const prev = STEPS[currentIdx - 1];
              if (prev) {
                setStep(prev);
              } else {
                onClose();
              }
            }}
            className="rounded-md border border-border px-4 py-2 text-sm"
          >
            {currentIdx === 0 ? "Cancel" : "Back"}
          </button>
          {step === "review" ? (
            <button
              type="button"
              onClick={handleSend}
              disabled={isSubmitting || !reviewApproved}
              className="rounded-md bg-primary px-6 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
            >
              {isSubmitting ? "Submitting..." : sendOption === "now" ? "Send Now" : "Schedule"}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => {
                const next = STEPS[currentIdx + 1];
                if (next) {
                  setStep(next);
                }
              }}
              disabled={!canNext()}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
            >
              Next
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function createStarterBlocks(): EmailModePuckComponent[] {
  const header = createEmailModeComponent("email_header");
  const intro = createEmailModeComponent("email_text");
  const button = createEmailModeComponent("email_button");

  return [
    {
      ...header,
      props: { ...header.props, text: "Hello {{firstName}}," },
    },
    {
      ...intro,
      props: {
        ...intro.props,
        text: "Add the main message, offer, or update for this campaign here.",
      },
    },
    {
      ...button,
      props: {
        ...button.props,
        text: "Learn more",
        href: "https://example.com",
      },
    },
  ];
}

function AudienceStep({ name, onNameChange, segmentId, onSegmentChange, segments, onCreateSegment }: {
  name: string;
  onNameChange: (value: string) => void;
  segmentId: string;
  onSegmentChange: (value: string) => void;
  segments: Array<{ id: string; name: string; cachedCount: number | null }>;
  onCreateSegment: (input: { name: string; description?: string; type: "dynamic" | "static" }) => Promise<void>;
}) {
  const [showCreateSegment, setShowCreateSegment] = useState(false);
  const [segmentName, setSegmentName] = useState("");
  const [segmentDescription, setSegmentDescription] = useState("");
  const [segmentType, setSegmentType] = useState<"dynamic" | "static">("static");
  const [isCreatingSegment, setIsCreatingSegment] = useState(false);
  const [segmentError, setSegmentError] = useState<string | null>(null);

  return (
    <div className="space-y-4">
      <div>
        <label className="mb-1 block text-sm font-medium">Campaign Name *</label>
        <input
          type="text"
          value={name}
          onChange={(event) => onNameChange(event.target.value)}
          placeholder="March Newsletter"
          autoFocus
          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
        />
      </div>
      <div>
        <div className="mb-1 flex items-center justify-between gap-3">
          <label className="block text-sm font-medium">Audience *</label>
          <button
            type="button"
            onClick={() => setShowCreateSegment((current) => !current)}
            className="text-xs font-medium text-primary"
          >
            {showCreateSegment ? "Hide new segment" : "+ New segment"}
          </button>
        </div>
        <select
          value={segmentId}
          onChange={(event) => onSegmentChange(event.target.value)}
          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
        >
          <option value="">Select a list or segment...</option>
          {segments.map((segment) => (
            <option key={segment.id} value={segment.id}>
              {segment.name} {segment.cachedCount != null ? `(${segment.cachedCount})` : ""}
            </option>
          ))}
        </select>
      </div>
      {showCreateSegment ? (
        <div className="space-y-3 rounded-lg border border-border bg-surface p-4">
          <div className="grid gap-3 md:grid-cols-2">
            <label className="space-y-1 text-sm">
              <span className="text-xs font-medium uppercase text-muted-foreground">Segment name</span>
              <input
                value={segmentName}
                onChange={(event) => setSegmentName(event.target.value)}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              />
            </label>
            <label className="space-y-1 text-sm">
              <span className="text-xs font-medium uppercase text-muted-foreground">Type</span>
              <select
                value={segmentType}
                onChange={(event) => setSegmentType(event.target.value as "dynamic" | "static")}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              >
                <option value="static">Static</option>
                <option value="dynamic">Dynamic</option>
              </select>
            </label>
          </div>
          <label className="space-y-1 text-sm">
            <span className="text-xs font-medium uppercase text-muted-foreground">Description</span>
            <textarea
              rows={3}
              value={segmentDescription}
              onChange={(event) => setSegmentDescription(event.target.value)}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            />
          </label>
          <div className="flex items-center gap-3">
            <button
              type="button"
              disabled={!segmentName.trim() || isCreatingSegment}
              onClick={async () => {
                setIsCreatingSegment(true);
                setSegmentError(null);
                try {
                  await onCreateSegment({
                    name: segmentName.trim(),
                    description: segmentDescription.trim() || undefined,
                    type: segmentType,
                  });
                  setSegmentName("");
                  setSegmentDescription("");
                  setShowCreateSegment(false);
                } catch (caughtError) {
                  setSegmentError(caughtError instanceof Error ? caughtError.message : "Failed to create segment.");
                } finally {
                  setIsCreatingSegment(false);
                }
              }}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
            >
              {isCreatingSegment ? "Creating..." : "Create audience"}
            </button>
            {segmentError ? <p className="text-sm text-red-600">{segmentError}</p> : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function ContentStep({
  contentMode,
  onContentModeChange,
  subject,
  onSubjectChange,
  builderBlocks,
  onBuilderBlocksChange,
  htmlContent,
  onHtmlContentChange,
  textContent,
  onTextContentChange,
  contentNotice,
  contentNoticeTone,
  onSaveTemplate,
  onSelectTemplate,
}: {
  contentMode: ContentMode;
  onContentModeChange: (value: ContentMode) => void;
  subject: string;
  onSubjectChange: (value: string) => void;
  builderBlocks: EmailModePuckComponent[];
  onBuilderBlocksChange: (blocks: EmailModePuckComponent[]) => void;
  htmlContent: string;
  onHtmlContentChange: (value: string) => void;
  textContent: string;
  onTextContentChange: (value: string) => void;
  contentNotice: string | null;
  contentNoticeTone: "neutral" | "success" | "error";
  onSaveTemplate: () => Promise<void>;
  onSelectTemplate: (template: EmailTemplate) => void;
}) {
  const [showTemplates, setShowTemplates] = useState(false);
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3">
        <button
          type="button"
          onClick={() => setShowTemplates(true)}
          className="flex-1 rounded-md border-2 border-dashed border-border py-3 text-sm text-muted-foreground transition-colors hover:border-primary hover:text-primary"
        >
          Browse Template Library
        </button>
        <button
          type="button"
          onClick={async () => {
            setIsSavingTemplate(true);
            await onSaveTemplate();
            setIsSavingTemplate(false);
          }}
          disabled={!subject.trim() || !htmlContent.trim() || isSavingTemplate}
          className="rounded-md border border-border px-4 py-3 text-sm font-medium text-foreground disabled:opacity-50"
        >
          {isSavingTemplate ? "Saving…" : "Save as Template"}
        </button>
      </div>

      {showTemplates && (
        <TemplateLibrary
          onSelect={(template) => {
            onSelectTemplate(template);
            setShowTemplates(false);
          }}
          onClose={() => setShowTemplates(false)}
        />
      )}

      {contentNotice ? (
        <div
          className={cn(
            "rounded-md border px-4 py-3 text-sm",
            contentNoticeTone === "error"
              ? "border-red-300 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-950/30 dark:text-red-300"
              : contentNoticeTone === "success"
                ? "border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-300"
                : "border-border bg-surface text-foreground",
          )}
        >
          {contentNotice}
        </div>
      ) : null}

      <div>
        <label className="mb-1 block text-sm font-medium">Subject Line *</label>
        <input
          type="text"
          value={subject}
          onChange={(event) => onSubjectChange(event.target.value)}
          placeholder="Your monthly update from {{businessName}}"
          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
        />
        <p className="mt-1 text-xs text-muted-foreground">Use {"{{firstName}}"} and {"{{businessName}}"} for personalization.</p>
      </div>

      <div>
        <div className="mb-1 flex items-center justify-between">
          <label className="text-sm font-medium">Email Content *</label>
          <div className="flex items-center gap-1 rounded-md border border-border p-1">
            {(["visual", "html"] as const).map((mode) => (
              <button
                key={mode}
                type="button"
                onClick={() => onContentModeChange(mode)}
                className={cn(
                  "rounded px-2 py-1 text-xs font-medium transition-colors",
                  contentMode === mode ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent",
                )}
              >
                {mode === "visual" ? "Visual" : "HTML"}
              </button>
            ))}
          </div>
        </div>
        {contentMode === "visual" ? (
          <EmbeddedEmailBuilder
            initialBlocks={builderBlocks}
            onChange={(next: EmbeddedEmailBuilderChange) => {
              onBuilderBlocksChange(next.blocks);
              onHtmlContentChange(sanitizeCampaignHtml(next.html));
            }}
          />
        ) : (
          <textarea
            value={htmlContent}
            onChange={(event) => onHtmlContentChange(sanitizeCampaignHtml(event.target.value))}
            rows={12}
            className="w-full rounded-md border border-border bg-background px-3 py-2 font-mono text-sm"
          />
        )}
        {contentMode === "visual" ? (
          <p className="mt-2 text-xs text-muted-foreground">
            Visual edits keep the stored campaign HTML synchronized with the mounted template payload.
          </p>
        ) : null}
      </div>

      <div>
        <label className="mb-1 block text-sm font-medium">Plain Text (optional)</label>
        <textarea
          value={textContent}
          onChange={(event) => onTextContentChange(event.target.value)}
          rows={4}
          placeholder="Auto-generated from HTML if left empty"
          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
        />
      </div>
    </div>
  );
}

function ScheduleStep({
  sendOption,
  onSendOptionChange,
  scheduledAt,
  onScheduledAtChange,
  scheduleTimezone,
  onScheduleTimezoneChange,
  supportedTimezones,
  sendTime,
  sendTimeLoading,
  sendTimeError,
}: {
  sendOption: SendOption;
  onSendOptionChange: (value: SendOption) => void;
  scheduledAt: string;
  onScheduledAtChange: (value: string) => void;
  scheduleTimezone: string;
  onScheduleTimezoneChange: (value: string) => void;
  supportedTimezones: string[];
  sendTime: SendTimeResponse | null;
  sendTimeLoading: boolean;
  sendTimeError: string | null;
}) {
  const recommendation = sendTime?.recommendation ?? null;

  return (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-3">
        <label className={cn("flex cursor-pointer items-center gap-2 rounded-md border p-4", sendOption === "now" ? "border-primary bg-primary/5" : "border-border")}>
          <input type="radio" name="send" value="now" checked={sendOption === "now"} onChange={() => onSendOptionChange("now")} className="sr-only" />
          <div>
            <p className="font-medium">Send Now</p>
            <p className="text-xs text-muted-foreground">Campaign will start immediately.</p>
          </div>
        </label>
        <label className={cn("flex cursor-pointer items-center gap-2 rounded-md border p-4", sendOption === "scheduled" ? "border-primary bg-primary/5" : "border-border")}>
          <input type="radio" name="send" value="scheduled" checked={sendOption === "scheduled"} onChange={() => onSendOptionChange("scheduled")} className="sr-only" />
          <div>
            <p className="font-medium">Schedule</p>
            <p className="text-xs text-muted-foreground">Choose the exact local time and timezone.</p>
          </div>
        </label>
        <label
          className={cn(
            "flex cursor-pointer items-center gap-2 rounded-md border p-4",
            sendOption === "optimal" ? "border-primary bg-primary/5" : "border-border",
            !recommendation && "cursor-not-allowed opacity-60",
          )}
        >
          <input
            type="radio"
            name="send"
            value="optimal"
            checked={sendOption === "optimal"}
            onChange={() => recommendation && onSendOptionChange("optimal")}
            disabled={!recommendation}
            className="sr-only"
          />
          <div>
            <p className="font-medium">Optimal Time</p>
            <p className="text-xs text-muted-foreground">
              {recommendation ? `Use the best-performing hour from mounted engagement data.` : "Available once send-time intelligence has enough data."}
            </p>
          </div>
        </label>
      </div>

      <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_220px]">
        {sendOption === "scheduled" ? (
          <div>
            <label className="mb-1 block text-sm font-medium">Scheduled Local Time</label>
            <input
              type="datetime-local"
              value={scheduledAt}
              onChange={(event) => onScheduledAtChange(event.target.value)}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            />
          </div>
        ) : (
          <div className="rounded-md border border-border bg-surface px-4 py-3 text-sm text-muted-foreground">
            {sendOption === "optimal" && recommendation
              ? `The campaign will queue for the next ${String(recommendation.optimalHour).padStart(2, "0")}:00 slot in ${scheduleTimezone}.`
              : "Immediate send bypasses scheduled delivery windows."}
          </div>
        )}

        <div>
          <label className="mb-1 block text-sm font-medium">Timezone</label>
          <select
            value={scheduleTimezone}
            onChange={(event) => onScheduleTimezoneChange(event.target.value)}
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
          >
            {supportedTimezones.map((timezone) => (
              <option key={timezone} value={timezone}>{timezone}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="rounded-md border border-border bg-surface px-4 py-4">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h3 className="text-sm font-semibold text-foreground">Send-time Intelligence</h3>
            <p className="mt-1 text-xs text-muted-foreground">
              {sendTime?.provenance.message ?? "Loading the mounted send-time recommendation…"}
            </p>
          </div>
          {recommendation ? (
            <div className="rounded-full border border-border px-3 py-1 text-xs font-semibold uppercase tracking-wide text-foreground">
              {String(recommendation.optimalHour).padStart(2, "0")}:00 · {recommendation.confidence}
            </div>
          ) : null}
        </div>
        {sendTimeLoading ? (
          <p className="mt-3 text-sm text-muted-foreground">Loading recommendation…</p>
        ) : sendTimeError ? (
          <p className="mt-3 text-sm text-red-600 dark:text-red-400">{sendTimeError}</p>
        ) : recommendation ? (
          <div className="mt-3 grid grid-cols-6 gap-2 sm:grid-cols-12">
            {recommendation.distribution.map((entry) => (
              <div key={entry.hour} className="rounded-md border border-border bg-background px-2 py-3 text-center">
                <div className="text-[10px] font-semibold text-muted-foreground">{String(entry.hour).padStart(2, "0")}</div>
                <div className="mt-2 text-sm font-medium text-foreground">{Math.round(entry.score * 100)}%</div>
              </div>
            ))}
          </div>
        ) : (
          <p className="mt-3 text-sm text-muted-foreground">
            No optimal slot is available yet. You can still schedule by local time and timezone.
          </p>
        )}
      </div>
    </div>
  );
}

function ReviewStep({
  name,
  subject,
  segmentId,
  segmentName,
  sendOption,
  scheduledAt,
  scheduleTimezone,
  optimalHour,
  htmlContent,
  reviewApproved,
  onReviewApprovedChange,
}: {
  name: string;
  subject: string;
  segmentId: string;
  segmentName: string;
  sendOption: SendOption;
  scheduledAt: string;
  scheduleTimezone: string;
  optimalHour: number | null;
  htmlContent: string;
  reviewApproved: boolean;
  onReviewApprovedChange: (approved: boolean) => void;
}) {
  const [lastRequestKey, setLastRequestKey] = useState("");
  const [feedbackStates, setFeedbackStates] = useState<Record<string, "positive" | "negative">>({});
  const prediction = useApiMutation<{
    subject: string;
    htmlContent: string;
    segmentId?: string;
    scheduledAt?: string | null;
    sendAtHour?: number | null;
  }, CampaignPredictionResponse>("/email-marketing/campaigns/intelligence/predict");

  useEffect(() => {
    const sendAtHour = sendOption === "optimal"
      ? optimalHour
      : sendOption === "scheduled" && scheduledAt
        ? Number.parseInt(scheduledAt.slice(11, 13), 10)
        : null;

    const request = {
      subject,
      htmlContent,
      segmentId: segmentId || undefined,
      scheduledAt: sendOption === "scheduled" && scheduledAt
        ? convertZonedLocalToUtcIso(scheduledAt, scheduleTimezone)
        : null,
      sendAtHour,
    };

    const requestKey = JSON.stringify(request);
    if (!subject.trim() || !htmlContent.trim() || requestKey === lastRequestKey) {
      return;
    }

    setLastRequestKey(requestKey);
    void prediction.mutate(request);
  }, [
    htmlContent,
    lastRequestKey,
    optimalHour,
    prediction.mutate,
    scheduleTimezone,
    scheduledAt,
    segmentId,
    sendOption,
    subject,
  ]);

  useEffect(() => {
    setFeedbackStates({});
  }, [htmlContent, scheduledAt, segmentId, sendOption, subject]);

  const handleRecommendationFeedback = async (
    recommendation: string,
    verdict: "positive" | "negative",
  ) => {
    const normalizedRecommendation = recommendation.trim();
    if (!normalizedRecommendation) {
      return;
    }

    try {
      await sendAiFeedbackSignal({
        signalType: normalizedRecommendation.toLowerCase().includes("subject")
          ? "email_campaign.subject_suggestion.feedback"
          : "email_campaign.recommendation.feedback",
        aiOutputId: `campaign-review:${subject.slice(0, 80)}:${normalizedRecommendation.slice(0, 80)}`,
        aiOutput: {
          subject,
          recommendation: normalizedRecommendation,
        },
        humanAction: {
          verdict,
        },
        context: {
          touchpoint: "email_campaign.review",
          segmentId: segmentId || null,
          sendOption,
        },
      });
      setFeedbackStates((current) => ({ ...current, [normalizedRecommendation]: verdict }));
    } catch {
      // Feedback capture must not interrupt send review.
    }
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium">Review Campaign</h3>
      <div className="space-y-2 rounded-md border border-border p-4 text-sm">
        <p><span className="text-muted-foreground">Name:</span> {name}</p>
        <p><span className="text-muted-foreground">Subject:</span> {subject}</p>
        <p><span className="text-muted-foreground">Audience:</span> {segmentName}</p>
        <p>
          <span className="text-muted-foreground">Send:</span>{" "}
          {formatScheduledSummary(sendOption, scheduledAt, scheduleTimezone, optimalHour)}
        </p>
      </div>
      <div className="rounded-xl border border-[var(--color-ai-border)] bg-[var(--color-ai-light)] p-5">
        <p className="mb-4 text-sm font-semibold text-[var(--color-ai)]">Pre-Send Campaign Intelligence</p>
        {prediction.isLoading && !prediction.data ? (
          <p className="text-sm text-muted-foreground">Modeling open and click performance from tenant history…</p>
        ) : prediction.error ? (
          <p className="text-sm text-red-600">{prediction.error}</p>
        ) : prediction.data ? (
          <div className="space-y-4">
            <div className="grid gap-3 md:grid-cols-3">
              <div className="rounded-lg bg-white/70 px-4 py-3">
                <div className="text-xs uppercase text-muted-foreground">Predicted Open Rate</div>
                <div className="mt-1 text-2xl font-semibold text-foreground">
                  {prediction.data.prediction.predictedOpenRatePct}%
                </div>
              </div>
              <div className="rounded-lg bg-white/70 px-4 py-3">
                <div className="text-xs uppercase text-muted-foreground">Predicted Click Rate</div>
                <div className="mt-1 text-2xl font-semibold text-foreground">
                  {prediction.data.prediction.predictedClickRatePct}%
                </div>
              </div>
              <div className="rounded-lg bg-white/70 px-4 py-3">
                <div className="text-xs uppercase text-muted-foreground">Confidence</div>
                <div className="mt-1 text-2xl font-semibold capitalize text-foreground">
                  {prediction.data.prediction.confidence}
                </div>
              </div>
            </div>
            <div className="rounded-lg bg-white/70 px-4 py-3 text-sm text-foreground">
              Audience estimate: {prediction.data.audience.estimatedRecipients.toLocaleString()} recipients from{" "}
              {prediction.data.audience.historicalSampleSize.toLocaleString()} historical sends.
            </div>
            {prediction.data.prediction.factors.length > 0 ? (
              <ul className="space-y-2 text-sm text-foreground">
                {prediction.data.prediction.factors.map((factor) => (
                  <li key={factor}>{factor}</li>
                ))}
              </ul>
            ) : null}
            <div className="space-y-2">
              <p className="text-sm font-medium text-foreground">Recommendations</p>
              <ul className="space-y-2 text-sm text-foreground">
                {prediction.data.recommendations.map((item) => (
                  <li key={item} className="rounded-lg bg-white/70 px-4 py-3">
                    <p>{item}</p>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
                        {item.toLowerCase().includes("subject") ? "Subject feedback" : "Feedback"}
                      </span>
                      <button
                        type="button"
                        onClick={() => { void handleRecommendationFeedback(item, "positive"); }}
                        className={`rounded-full border px-2.5 py-1 text-[11px] transition-colors ${
                          feedbackStates[item] === "positive"
                            ? "border-[var(--color-success)] bg-[var(--color-success)]/10 text-[var(--color-success)]"
                            : "border-border text-muted-foreground hover:bg-[var(--color-success)]/10 hover:text-[var(--color-success)]"
                        }`}
                      >
                        Helpful
                      </button>
                      <button
                        type="button"
                        onClick={() => { void handleRecommendationFeedback(item, "negative"); }}
                        className={`rounded-full border px-2.5 py-1 text-[11px] transition-colors ${
                          feedbackStates[item] === "negative"
                            ? "border-[var(--color-destructive)] bg-[var(--color-destructive)]/10 text-[var(--color-destructive)]"
                            : "border-border text-muted-foreground hover:bg-[var(--color-destructive)]/10 hover:text-[var(--color-destructive)]"
                        }`}
                      >
                        Off target
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">Prediction will appear once the review inputs are ready.</p>
        )}
      </div>
      <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950 dark:border-amber-900/60 dark:bg-amber-950/20 dark:text-amber-100">
        <label className="flex items-start gap-3">
          <input
            type="checkbox"
            checked={reviewApproved}
            onChange={(event) => onReviewApprovedChange(event.target.checked)}
            className="mt-1"
          />
          <span>
            <span className="block font-medium">Human review required before sending</span>
            <span className="mt-1 block text-xs">
              I reviewed the subject, audience, send timing, unsubscribe/compliance requirements, and AI-assisted recommendations before queueing this campaign.
            </span>
          </span>
        </label>
      </div>
      <div>
        <p className="mb-2 text-sm font-medium text-muted-foreground">Preview</p>
        <EmailPreview html={htmlContent} title="Campaign preview" />
      </div>
    </div>
  );
}

export {
  sanitizeCampaignHtml,
};
